import { Form, Formik, type FormikHelpers } from 'formik';
import { memo, useCallback, useEffect, useState } from 'react';
import { object, string } from 'yup';

import { type Workspace } from '@air/api/types';
import { InfoIcon } from '@air/design-icons';
import { FormikField } from '@air/formik-field';
import { Button } from '@air/primitive-button';
import { Input } from '@air/primitive-input';
import { Modal, ModalTitle } from '@air/primitive-modal';
import { Tooltip } from '@air/primitive-tooltip';
import { uuidRegExpString } from '@air/utils-regex';

import { SYSADMIN_SET_WORKSPACE_MODAL_LOCAL_STORAGE_KEY } from './constants/sysadminLocalStorage';
import { useSysadmin } from './SysadminProvider';

export { SYSADMIN_SET_WORKSPACE_MODAL_LOCAL_STORAGE_KEY };

export const SYSADMIN_SET_WORKSPACE_MODAL_VALIDATION = object().shape({
  workspaceId: string()
    .matches(new RegExp(`.*(${uuidRegExpString}).*`, 'i'), 'Invalid workspace ID')
    .required('Workspace ID is required'),
});

export type SysadminSetWorkspaceModalValues = {
  workspaceId: string;
};

export const InternalSetWorkspaceModal = memo(() => {
  const { handleSetWorkspaceId } = useSysadmin();
  const [recentlyUsed, setRecentlyUsed] = useState<Pick<Workspace, 'id' | 'name'>[]>([]);

  const handleClearRecentlyUsed = useCallback(() => {
    localStorage.removeItem(SYSADMIN_SET_WORKSPACE_MODAL_LOCAL_STORAGE_KEY);
    setRecentlyUsed([]);
  }, []);

  const handleSetWorkspace = useCallback(
    async (
      { workspaceId }: SysadminSetWorkspaceModalValues,
      formikHelpers?: FormikHelpers<SysadminSetWorkspaceModalValues>,
    ) => {
      formikHelpers?.setSubmitting(true);

      if (workspaceId) {
        handleSetWorkspaceId(workspaceId, true);
      }

      formikHelpers?.setSubmitting(true);
    },
    [handleSetWorkspaceId],
  );

  /**
   * Load recently used workspaces from local storage.
   */
  useEffect(() => {
    const recentlyUsed = localStorage.getItem(SYSADMIN_SET_WORKSPACE_MODAL_LOCAL_STORAGE_KEY);

    if (recentlyUsed) {
      setRecentlyUsed(JSON.parse(recentlyUsed));
    }
  }, []);

  /**
   * If the user has a workspace id in the URL, we'll set the workspace id
   * regardless if the user opens the modal or not.
   */
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const workspaceId = searchParams.get('wsid');

    if (workspaceId) {
      void handleSetWorkspace({ workspaceId });
    }
  }, [handleSetWorkspace]);

  return (
    <Modal isOpen>
      <header className="-mx-6 -mt-6 mb-6 rounded-t-6 border-b border-b-gray-5 bg-gray-1 p-6">
        <ModalTitle>Workspace ID</ModalTitle>
      </header>
      <div className="flex flex-col gap-6">
        <Formik<SysadminSetWorkspaceModalValues>
          initialValues={{ workspaceId: '' }}
          onSubmit={handleSetWorkspace}
          validationSchema={SYSADMIN_SET_WORKSPACE_MODAL_VALIDATION}
        >
          {({ isSubmitting }) => (
            <Form className="flex flex-col gap-6">
              <FormikField
                component={<Input required size="large" type="text" />}
                id="workspaceId"
                label="Workspace ID"
                name="workspaceId"
              />
              <Button isLoading={isSubmitting} size="large" type="submit">
                Go to workspace
              </Button>
            </Form>
          )}
        </Formik>

        {!!recentlyUsed.length && (
          <>
            <div className="h-px w-full bg-gray-5" />

            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-14">Recently used</span>
                <button
                  className="border-0 bg-none font-semibold text-12 hover:underline"
                  onClick={handleClearRecentlyUsed}
                  type="button"
                >
                  Clear all
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {recentlyUsed.map((workspace) => (
                  <button
                    className="flex h-8 cursor-pointer items-center gap-2 rounded-4 border border-gray-5 bg-gray-3 px-3 font-semibold text-12"
                    key={workspace.id}
                    // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
                    onClick={() => handleSetWorkspace({ workspaceId: workspace.id })}
                    type="button"
                  >
                    {workspace.name}

                    <Tooltip label={workspace.id}>
                      <InfoIcon size="sm" />
                    </Tooltip>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
});

InternalSetWorkspaceModal.displayName = 'InternalSetWorkspaceModal';
